import React, { useState, useEffect, useCallback } from 'react';
import { 
  ShieldAlert, CheckCircle, Clock, Plus, Search, 
  RefreshCw, User, Calendar, Trash2, ShieldCheck, MapPin, CheckCircle2,
  ChevronLeft, ChevronRight 
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

const CATEGORIES = [
  'Maintenance',
  'Discipline',
  'Student Welfare',
  'General'
];

const WardenIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all, pending, resolved
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({ all: 0, pending: 0, in_progress: 0, resolved: 0 });

  // Log Issue Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    issue_type: 'Maintenance',
    title: '',
    description: '',
    severity: 'Normal',
    room_number: ''
  });

  const fetchIssues = useCallback(async (targetPage = page, targetSearch = search) => {
    setLoading(true);
    try {
      let url = `/v1/warden/issues/?page=${targetPage}&limit=${limit}`;
      if (activeTab !== 'all') {
        url += `&status=${activeTab}`;
      }
      if (targetSearch.trim()) {
        url += `&search=${encodeURIComponent(targetSearch.trim())}`;
      }
      const res = await apiClient.get(url);
      const data = res.data;
      if (data) {
        setIssues(data.issues || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.total_pages || 1);
        if (data.counts) {
          setCounts(data.counts);
        }
      }
    } catch (err) {
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, limit, search]);

  useEffect(() => {
    fetchIssues(page, search);
  }, [activeTab, page, limit]);

  // Debounced search trigger (resets page to 1)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchIssues(1, search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setPage(1);
  };

  const handleCreateIssue = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post('/v1/warden/issues/create', formData);
      if (res.data?.success) {
        toast.success('Issue logged to Admin successfully');
        setShowCreateModal(false);
        setFormData({
          issue_type: 'Maintenance',
          title: '',
          description: '',
          severity: 'Normal',
          room_number: ''
        });
        setPage(1);
        fetchIssues(1, search);
      } else {
        toast.error(res.data?.message || 'Failed to submit issue');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log issue');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Delete this pending issue?')) return;
    try {
      const res = await apiClient.delete(`/v1/warden/issues/${issueId}`);
      if (res.data?.success) {
        toast.success('Issue removed');
        fetchIssues(page, search);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete issue');
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'critical':
        return <Badge className="bg-rose-600 text-white font-semibold">Urgent</Badge>;
      case 'high':
        return <Badge className="bg-amber-500 text-white font-semibold">High</Badge>;
      default:
        return <Badge variant="outline" className="text-slate-600">Normal</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" /> Pending</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-600 text-white gap-1">In Progress</Badge>;
      case 'resolved':
        return <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle className="w-3 h-3" /> Resolved</Badge>;
      default:
        return <Badge variant="outline">{status?.toUpperCase() || 'LOGGED'}</Badge>;
    }
  };

  const pendingCount = (counts.pending || 0) + (counts.in_progress || 0);
  const resolvedCount = counts.resolved || 0;
  const totalDisplayCount = counts.all || 0;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="warden-issues-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-amber-600" />
            Warden Issues
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Report maintenance, discipline, and student issues directly to Administration
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => fetchIssues(page, search)} variant="outline" size="sm" className="gap-1.5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4" /> New Issue
          </Button>
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
        {/* Filter Pills */}
        <div className="flex items-center gap-2 bg-slate-100/80 p-1.5 rounded-xl text-sm font-medium">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              activeTab === 'all' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All ({totalDisplayCount})
          </button>

          <button
            onClick={() => handleTabChange('pending')}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              activeTab === 'pending' ? 'bg-white text-amber-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Pending ({pendingCount})
          </button>

          <button
            onClick={() => handleTabChange('resolved')}
            className={`px-3.5 py-1.5 rounded-lg transition-all ${
              activeTab === 'resolved' ? 'bg-white text-emerald-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Resolved ({resolvedCount})
          </button>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search issues..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Issues List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading issues...</div>
        ) : issues.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <CheckCircle2 className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-700 font-semibold">No issues found</p>
            <p className="text-slate-400 text-xs mt-1">
              {search ? 'No issues matching your search.' : 'Click "+ New Issue" to report a maintenance or hostel matter to the admin.'}
            </p>
          </Card>
        ) : (
          issues.map((issue) => (
            <Card key={issue.id} className="border border-slate-200 hover:shadow-sm transition-all overflow-hidden bg-white">
              <CardContent className="p-5 space-y-3">
                {/* Header Row */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-800 text-white">{issue.issue_type}</Badge>
                    {getPriorityBadge(issue.severity)}
                    {getStatusBadge(issue.status)}
                  </div>

                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(issue.created_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Title & Description */}
                <div>
                  <h3 className="text-base font-bold text-slate-900">{issue.title}</h3>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1">
                    {issue.description}
                  </p>
                </div>

                {/* Optional Location / Student */}
                {(issue.room_number || issue.student_name) && (
                  <div className="flex flex-wrap gap-3 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    {issue.room_number && (
                      <span className="flex items-center gap-1 font-semibold text-slate-700">
                        <MapPin className="w-3.5 h-3.5 text-amber-600" /> Location / Room: {issue.room_number}
                      </span>
                    )}
                    {issue.student_name && (
                      <span className="flex items-center gap-1 font-semibold text-slate-700">
                        <User className="w-3.5 h-3.5 text-blue-600" /> Student: {issue.student_name}
                      </span>
                    )}
                  </div>
                )}

                {/* Admin Resolution Remarks */}
                {(issue.resolution_note || issue.status === 'resolved') && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 flex items-start gap-2.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-0.5 flex-1">
                      <span className="font-bold text-emerald-800">Admin Resolution:</span>
                      <p className="text-emerald-900 font-semibold">
                        {issue.resolution_note || 'Resolved by Administrator.'}
                      </p>
                      {issue.resolved_by_name && (
                        <p className="text-[11px] text-emerald-600 pt-0.5">
                          Resolved by {issue.resolved_by_name} {issue.resolved_at ? `on ${new Date(issue.resolved_at).toLocaleDateString()}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {issue.status === 'pending' && (
                  <div className="flex justify-end pt-1">
                    <Button
                      onClick={() => handleDeleteIssue(issue.id)}
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:bg-rose-50 text-xs gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Cancel Issue
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm text-sm">
          <div className="flex flex-wrap items-center gap-2 text-slate-500 text-xs">
            <span>
              Showing <strong className="text-slate-800 font-bold">{Math.min((page - 1) * limit + 1, totalCount)}</strong> - <strong className="text-slate-800 font-bold">{Math.min(page * limit, totalCount)}</strong> of <strong className="text-slate-800 font-bold">{totalCount}</strong> issues
            </span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Show:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 font-medium outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || loading}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="h-8 px-2.5 text-xs text-slate-700 gap-1 border-slate-200 hover:bg-slate-50"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1))
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    return (
                      <React.Fragment key={p}>
                        {prev && p - prev > 1 && (
                          <span className="px-1 text-slate-400 text-xs">...</span>
                        )}
                        <button
                          onClick={() => setPage(p)}
                          disabled={loading}
                          className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${
                            page === p
                              ? 'bg-amber-600 text-white shadow-sm'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages || loading}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                className="h-8 px-2.5 text-xs text-slate-700 gap-1 border-slate-200 hover:bg-slate-50"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Simple Log New Issue Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Report Issue to Admin
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateIssue} className="space-y-4 pt-2">
            {/* Category & Priority Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="block text-xs font-bold text-slate-600 mb-1">Category *</Label>
                <select
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={formData.issue_type}
                  onChange={(e) => setFormData({ ...formData, issue_type: e.target.value })}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="block text-xs font-bold text-slate-600 mb-1">Priority *</Label>
                <select
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                >
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Location / Student (Optional) */}
            <div>
              <Label className="block text-xs font-bold text-slate-600 mb-1">Location or Student (Optional)</Label>
              <Input
                placeholder="e.g. Room 204, 2nd Floor, or Student Name"
                value={formData.room_number}
                onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
              />
            </div>

            {/* Title */}
            <div>
              <Label className="block text-xs font-bold text-slate-600 mb-1">Issue Subject *</Label>
              <Input
                required
                placeholder="e.g. Broken water tap in 2nd floor bathroom"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* Description */}
            <div>
              <Label className="block text-xs font-bold text-slate-600 mb-1">Details *</Label>
              <textarea
                rows={3}
                required
                placeholder="Describe the issue and what action is needed..."
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-500"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting} 
                className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              >
                {submitting ? 'Submitting...' : 'Submit Issue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WardenIssues;
